// @ts-nocheck
import type React from "react";
import { useEffect, useMemo, useState } from "react";

import { SocialTotalVisits } from "app/pages/website-analysis/social-overview/social-total-visits/socialTotalVisits";
import { SocialOverviewSourcesDistribution } from "app/pages/digital-marketing/social/SocialOverviewSourcesDistribution";
import organicPaidService from "app/common/services/organicPaid";
import { useLoading } from "app/custom-hooks/loadingHook";
import { useCurrentStateAndParams } from "@uirouter/react";
import { SocialOverviewLineChart } from "app/pages/digital-marketing/social/SocialOverviewLineChart";
import { SocialOverviewPageTableTop } from "./SocialOverviewPageTableTop";
import * as _ from "lodash";
import queryString from "query-string";
import { apiHelper } from "app/common/services/apiHelper";
import { chosenSites } from "app/common/services/chosenSites";
import { GetSocialOverTimeEnrichedRow } from "app/components/React/SocialOvertime/SocialOverTimeEnrichedRow";
import SWReactTableWrapper from "app/components/React/Table/SWReactTableWrapper";
import { minAbbrNumberFilter } from "app/filters/ngFilters";
import { getSocialOverviewColumns } from "app/pages/digital-marketing/social/SocialOverviewColumns";
import type { ISocialTotalVisitsData } from "app/pages/website-analysis/social-overview/social-total-visits/socialTotalVisits";
import DurationService from "app/services/DurationService";
import type { dateForTooltip } from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { allTrackers } from "app/services/track/track";

export const SocialOverviewPage: React.FC<any> = () => {
    const { params } = useCurrentStateAndParams();
    const { country, isWWW, key, duration } = params;
    const durationObject = DurationService.getDurationData(duration);
    const getDateRange = (durationObject) => [durationObject.raw.from, durationObject.raw.to];
    const [fromDate, toDate] = getDateRange(durationObject);
    const isCompare = params.key.split(",").length > 1;
    const defaultSort = isCompare ? "TotalShare" : "Share";
    const [tableData, setTableData] = useState(null);
    const [tableDateError, setTableDateError] = useState(false);
    const [overviewData, overviewDataOps, overviewDataIsLoading] = useLoading<ISocialTotalVisitsData>();
    const [tableOverviewData, setTableOverviewData] = useState(null);
    useEffect(() => {
        overviewDataOps.load(async () => {
            const { from, to, isWindow } = durationObject.forAPI;
            const API = `api/websiteanalysis/GetTrafficSocial`;
            const response = await DefaultFetchService.getInstance().get<any>(API, {
                country,
                from,
                isWWW: isWWW === "-",
                isWindow,
                key,
                to,
                page: 1,
                orderby: `${defaultSort} desc`,
            });
            if (!isCompare) {
                const { total } = organicPaidService.calculateOrganicVsPaid(response.dictionary[key].Volumes);
                setTableOverviewData({ total });
            } else {
                const total = chosenSites.calcTotal(response.dictionary, "Volumes");
                setTableOverviewData({ total });
            }

            return response;
        });
    }, [params]);
    const columns = useMemo(() => {
        const durationTooltip = _.mapValues(
            DurationService.getDurationData(duration || "", "", "").forTooltip as dateForTooltip,
            (v) => {
                return decodeURIComponent(v);
            },
        );
        const durationTooltipParams = {
            currentMonth: durationTooltip.to,
            lastMonth: durationTooltip.from,
        };
        return getSocialOverviewColumns({
            compare: isCompare,
            sortedColumn: defaultSort,
            sortDirection: "desc",
            durationTooltipParams,
        });
    }, [params]);
    const initialFilters = apiHelper.transformParamsForAPI(params);
    initialFilters.sort = params.sort || defaultSort;
    initialFilters.asc = params.asc === "true" || false;
    initialFilters.search = params.search;
    initialFilters.source = params.source;

    const transformData = (data) => {
        (data.Records || []).forEach((row) => {
            row.SiteOrigins = Object.entries(row.SiteOrigins).map(([name, value], index) => {
                return {
                    name,
                    value,
                    index,
                };
            });
        });
        return data;
    };
    const onGetData = (data) => {
        setTableData(data);
    };
    const onDataError = () => {
        setTableDateError(true);
    };
    const paramsAdapter = (params) => {
        const result = { ...params };
        const filters = [];
        if (params.source) {
            filters.push(`socialNetwork;==;"${params.source}"`);
            delete result.source;
        }
        if (params.search) {
            filters.push(`page;contains;"${params.search}"`);
            delete result.search;
        }

        if (filters.length > 0) {
            result.filter = filters.join(",");
        }

        // orderby
        result.orderby = `${params.sort || defaultSort} ${params.asc ? "asc" : "desc"}`;
        delete result.sort;
        delete result.asc;
        delete result.orderBy;

        return result;
    };

    const getExcelUrl = (filters) => {
        const transforemd = paramsAdapter(filters);
        const queryStringParams = queryString.stringify(transforemd);
        return `/export/analysis/GetTrafficSocialTsv?${queryStringParams}`;
    };

    const shareTextByWebSource: Record<string, string> = {
        Desktop: "analysis.totalvisits.span",
        MobileWeb: "analysis.totalvisits.mobile.span",
        Total: "analysis.totalvisits.span.alltraffic",
    };
    const shareText = shareTextByWebSource[params.webSource] ?? "analysis.totalvisits.span";

    const subTitleFilters = [
        {
            filter: "date",
            value: {
                from: fromDate.valueOf(),
                to: toDate.valueOf(),
                useRangeDisplay: fromDate.format("YYYY-MM") !== toDate.format("YYYY-MM"),
            },
        },
        {
            filter: "webSource",
            value: params.webSource,
        },
    ];

    const getTableTopComponent = (topComponentProps) => {
        if (topComponentProps.isLoadingData || !tableData || !tableOverviewData) {
            return null;
        }
        return (
            <SocialOverviewPageTableTop
                {...topComponentProps}
                tableData={tableData}
                sources={overviewData.data?.Sources}
                overviewData={tableOverviewData}
                excelLink={getExcelUrl(topComponentProps.filtersStateObject)}
                isCompare={isCompare}
            />
        );
    };
    return (
        <div
            className="sharedTooltip"
            data-automation="social-single"
        >
            {!isCompare ? (
                <div
                    className="row-fluid"
                    style={{ marginBottom: 24 }}
                >
                    <div className="span4">
                        <SocialTotalVisits
                            type="social"
                            loading={overviewDataIsLoading}
                            data={overviewData.data}
                            share={
                                overviewData.data?.dictionary[key].SearchTotal /
                                overviewData.data?.dictionary[key].VolumeTotal
                            }
                            height={"325px"}
                            subTitleFilters={subTitleFilters}
                            shareText={shareText}
                            numberFormat={minAbbrNumberFilter()}
                        />
                    </div>
                    <div className="span8">
                        <SocialOverviewSourcesDistribution
                            data={tableData?.TopSources}
                            loading={!tableData}
                            error={tableDateError}
                            duration={duration}
                            webSource={params.webSource}
                        />
                    </div>
                </div>
            ) : null}
            <div
                className="row-fluid"
                style={{ marginBottom: 24 }}
            >
                <div className="span12">
                    <SocialOverviewLineChart
                        rawData={overviewData.data?.dictionary}
                        webSource={params.webSource}
                        from={DurationService.getDurationData(params.duration).raw.from}
                        loading={overviewDataIsLoading}
                        error={overviewData.error}
                        isCompare={isCompare}
                        duration={duration}
                    />
                </div>
            </div>
            <SWReactTableWrapper
                tableOptions={{
                    metric: "SocialOverview",
                    defaultSorted: defaultSort,
                    showCompanySidebar: true,
                    get EnrichedRowComponent() {
                        return GetSocialOverTimeEnrichedRow({
                            page: 1,
                            pageSize: 100,
                        });
                    },
                    shouldEnrichRow: (props, index, e) => {
                        const openEnrich = e?.currentTarget?.childNodes[0]?.className.includes("enrich");
                        if (openEnrich) {
                            allTrackers.trackEvent("Open", "Click", "Traffic Over Time/Expand");
                        }
                        return openEnrich;
                    },
                    get enrichedRowComponentHeight() {
                        const width = window.innerWidth;
                        if (width < 1200 && width > 320) {
                            return 625;
                        }
                        if (width <= 320) {
                            return 685;
                        } else {
                            return 580;
                        }
                    },

                    onEnrichedRowClick: (isOpen, rowIndex, row) => {
                        if (row === undefined) {
                            return;
                        }
                    },
                    get enrichedRowComponentAppendTo() {
                        return ".social-traffic-table";
                    },
                    shouldApplyEnrichedRowHeightToCell: false,
                    customTableClass: "social-traffic-table",
                }}
                serverApi={`/api/websiteanalysis/GetTrafficSocialTable`}
                tableColumns={columns}
                initialFilters={initialFilters}
                recordsField="Records"
                totalRecordsField="TotalCount"
                pageIndent={1}
                transformData={transformData}
                getDataCallback={onGetData}
                onDataError={onDataError}
                dataParamsAdapter={paramsAdapter}
            >
                {getTableTopComponent}
            </SWReactTableWrapper>
        </div>
    );
};
